/**
 * The dot, and the hook behind it.
 *
 * The part that carries meaning — which colour, and whether do-not-disturb
 * gets its bar — is `presenceDot()` in `@shared/presence`, so five apps cannot
 * disagree about it. What is here is the two elements around it and the
 * subscription, because five apps draw five differently-shaped chips and a
 * shared component would arrive with five props describing where it was.
 *
 * The socket itself is `@shared/presence-client`, started once by `state.tsx`
 * and pointed at this app's own proxy (`worker/presence.ts`).
 */

import React, { useSyncExternalStore } from 'react';

import { getPresence, getPresenceOverride, subscribePresence } from '@shared/presence-client';
import { presenceDot, type Presence } from '@shared/presence';

/**
 * What to draw, and what was chosen.
 *
 * Both are plain strings, so the snapshot is referentially stable and
 * `useSyncExternalStore` has nothing to loop on — the failure mode
 * `apply-branding.test.ts` beside it was written to pin.
 */
export function usePresence(): { presence: Presence; override: Presence | null } {
  const presence = useSyncExternalStore(subscribePresence, getPresence, () => 'unknown' as const);
  const override = useSyncExternalStore(subscribePresence, getPresenceOverride, () => null);
  return { presence, override };
}

/**
 * A dot in the corner of an avatar.
 *
 * The caller supplies the positioned wrapper — this is `position: absolute` and
 * needs something to be absolute inside. `ring` is the **chip's** background,
 * not the page's: the dot cuts itself out of what it sits on, and in this app
 * that is `--surface-sunken` on the account chip and `--surface-card` inside
 * the popover it opens.
 *
 * Renders nothing for `unknown`, which is what a page that has not heard yet
 * shows.
 */
export function PresenceDot({
  presence,
  size = 9,
  ring
}: {
  presence: Presence;
  size?: number;
  ring: string;
}) {
  const style = presenceDot(presence, size, ring);
  if (!style) return null;

  return (
    <span aria-hidden="true" style={style.dot as React.CSSProperties}>
      {style.bar && <span style={style.bar as React.CSSProperties} />}
    </span>
  );
}
